<template>
  <div v-if="!hasError">
    <request-review-popup
      :model-value="showReviewModal"
      :sitter-id="sitterId"
      :sitter-name="sitterName"
      @close="() => (showReviewModal = false)"
    />
    <div v-if="profileCompleteness">
      <div class="flex flex-wrap gap-12">
        <div class="max-w-max md:max-w-[65%]">
          <p class="text-h4 mb-16">
            Profile progress
            <warning-outline-icon
              v-if="showBgcExpiringState"
              class="inline-block ml-4 mb-6"
              color="#A87B00"
              :width="16"
              :height="16"
            />
          </p>
          <p v-if="showBgcExpiringState" class="text-body w-full">
            Your background check is expiring in {{ bgcExpirationDaysText }}.
            <a href="/account/providers/background-checks" class="text-warning-contrast underline">Update now.</a>
          </p>
          <p v-else class="text-body w-full">
            <span v-if="completedProfile">Nice work!</span>
            Sitters who complete their profile are at least <span class="font-medium">4x more</span> likely to be hired.
          </p>
        </div>
        <div class="min-w-[146px] max-w-max m-auto md:mr-16">
          <completeness-meter
            :completeness-percentage="completedProfile ? 100 : profileCompleteness?.profileCompletenessScore"
            :warning="showBgcExpiringState"
          />
        </div>
      </div>
      <div class="mt-24">
        <div v-if="completedProfile">
          <p class="text-h4 mb-16">Tips for you to stay ahead</p>
          <div class="flex flex-col md:flex-row md:gap-16">
            <ul class="list-disc w-full md:w-50">
              <li class="ml-16">Respond to inquiries within 24 hours</li>
              <li class="ml-16 mt-16">
                <a href="https://apps.apple.com/us/app/find-child-care-sittercity/id900660780" target="_blank" class="underline"
                  >Download the iOS app</a
                >
                for jobs on the go
              </li>
            </ul>
            <ul class="list-disc w-full md:w-50 mt-16 md:mt-0">
              <li class="ml-16">
                Always follow up & ask for a <a class="underline" href="#" @click.prevent="showReviewModal = true">review</a>
              </li>
              <li class="ml-16 mt-16">
                <a class="underline" :href="stepLinkMap.ACTIVE_APPLICANT">Search</a> and apply for jobs frequently
              </li>
            </ul>
          </div>
        </div>
        <step-card-list v-else :items="steps" :collapsible-filter="(item) => item.isCollapsible" />
      </div>
      <profile-confetti :show-confetti="showConfetti" />
    </div>
    <spinner v-if="isLoading" class="m-auto mt-64" :width="48" :height="48" />
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import CompletenessMeter from '@/components/completeness_meter/CompletenessMeter.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import StepCardList from '@/components/step_card_list/StepCardList.vue';
import { FETCH_PROFILE_COMPLETENESS, FETCH_SITTER_PROFILE_BADGES } from '@/graphql/provider/profile_progress/queries';
import RequestReviewPopup from '../RequestReviewPopup.vue';
import ProfileConfetti from './ProfileConfetti.vue';
import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import { usesSpaRoutes } from '@/apps/provider/profile/helpers/profileEditUrlHelper';

export default {
  name: 'ProfileProgress',
  components: { CompletenessMeter, StepCardList, Spinner, RequestReviewPopup, ProfileConfetti, WarningOutlineIcon },
  data() {
    return {
      hasError: false,
      profileCompleteness: null,
      sitterProfile: null,
      isLoading: true,
      showReviewModal: false,
      showConfetti: false,
    };
  },
  computed: {
    completedProfile() {
      return this.profileCompleteness?.profileCompletenessScore === 100;
    },
    daysUntilLatestBgcExpiration() {
      const badges = this.sitterProfile?.badges;
      if (!badges) return null;

      const activeBgcs = badges.filter(
        (b) => (b.name === 'basic_criminal' || b.name === 'enhanced_criminal' || b.name === 'motor_vehicle') && b.expiresAt
      );

      if (activeBgcs.length === 0) return null;

      const latestExpirationDate = new Date(Math.max(...activeBgcs.map((b) => new Date(b.expiresAt))));
      const daysUntilExpiration = Math.ceil((latestExpirationDate - new Date()) / (1000 * 60 * 60 * 24));

      return daysUntilExpiration;
    },
    showBgcExpiringState() {
      if (!this.completedProfile) return false;
      const days = this.daysUntilLatestBgcExpiration;
      return days !== null && days > 0 && days <= 10;
    },
    bgcExpirationDaysText() {
      return pluralize('day', this.daysUntilLatestBgcExpiration, true);
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    sitterName() {
      return `${this.account.firstName} ${this.account.lastName ? this.account.lastName[0] : ''}.`;
    },
    sitterId() {
      return this.account.id;
    },
    shouldUseSpaRoutes(): boolean {
      return usesSpaRoutes(this.profileCompleteness?.profileCareType?.toLowerCase() || '');
    },
    stepLinkMap() {
      const careType = this.profileCompleteness?.profileCareType?.toLowerCase();
      const baseURL = `/profile/provider/${careType}`;
      const isChildCare = careType === 'babysitter' || careType === 'nanny';
      const genericCareTypeForUrls = isChildCare ? 'child_care' : careType;
      return {
        PHOTO: '/sitter/photo_redirect?return_to_dashboard=true',
        YEARS_OF_EXPERIENCE: this.shouldUseSpaRoutes
          ? `/provider/profile/${careType}/edit/experience`
          : `${baseURL}/${genericCareTypeForUrls}_experience/edit?return_to_dashboard=true`,
        AVAILABILITY: `/profile/provider/${careType}/${careType}_availability/edit?return_to_dashboard=true`,
        IDV: '/sitter/verify_identity',
        BIO_COMPLETE: this.shouldUseSpaRoutes
          ? `/provider/profile/${careType}/edit/bio`
          : `${baseURL}/${careType}_profile/edit?return_to_dashboard=true`,
        ACTIVE_APPLICANT: `/provider/jobs/search?careType=${careType}`,
        ACCESSED_APP: 'https://apps.apple.com/us/app/find-child-care-sittercity/id900660780',
        DETAILED_BIO: this.shouldUseSpaRoutes
          ? `/provider/profile/${careType}/edit/bio`
          : `${baseURL}/${careType}_profile/edit?return_to_dashboard=true`,
        RATINGS_AND_REVIEWS: undefined,
        BACKGROUND_CHECK: '/account/providers/background-checks',
      };
    },
    steps() {
      const steps = [];
      this.profileCompleteness?.steps.forEach((step) => {
        if (step.isComplete) {
          steps.push(this.getStep(step));
        } else {
          steps.unshift(this.getStep(step));
        }
      });
      if (steps[0]?.variant === 'incompleteTask') steps[0].isCollapsible = false;
      if (steps[1]?.variant === 'incompleteTask') steps[1].isCollapsible = false;
      if (steps[2]?.variant === 'incompleteTask') steps[2].isCollapsible = false;
      return steps;
    },
  },
  watch: {
    profileCompleteness(newValue) {
      if (newValue?.profileCompletenessScore !== 100) {
        sessionStorage.removeItem('confetti_shown');
      }
      this.showConfetti =
        newValue?.profileCompletenessScore === 100 && sessionStorage.getItem('confetti_shown') !== this.sitterId;
      if (this.showConfetti) {
        setTimeout(() => {
          this.showConfetti = false;
          sessionStorage.setItem('confetti_shown', this.sitterId);
        }, 2500);
      }
    },
  },
  methods: {
    getStep(step) {
      const linkURL = !step.isComplete ? (this.stepLinkMap[step.attributeType] ?? undefined) : undefined;
      return {
        variant: step.isComplete ? 'completedTask' : 'incompleteTask',
        title: step.title,
        content: step.description,
        linkURL,
        disabled: step.isComplete,
        isCollapsible: true,
        useFullPageNavigation: !!linkURL,
        modalComponent: step.attributeType === 'RATINGS_AND_REVIEWS' ? RequestReviewPopup : undefined,
        modalProps:
          step.attributeType === 'RATINGS_AND_REVIEWS' ? { sitterId: this.sitterId, sitterName: this.sitterName } : undefined,
      };
    },
  },
  apollo: {
    profileCompleteness: {
      query: FETCH_PROFILE_COMPLETENESS,
      result() {
        this.isLoading = false;
      },
      error(err) {
        this.hasError = true;
        // @ts-ignore
        console.error(err);
      },
    },
    sitterProfile: {
      query: FETCH_SITTER_PROFILE_BADGES,
      error(err) {
        console.error(err);
      },
    },
  },
};
</script>
